<script setup lang="ts">
// "Usar na loja" sai do painel (/vip/store não é uma tela /user/*) — sem
// fechar, o overlay do modal fica por cima do destino (mesmo padrão do
// onShortcutClick em ScreenProfile.vue)
const { close, open } = useUserV2Modal();

// resolveComponent só funciona no setup — no template ele resolve pra
// string e o card vira elemento morto (não navega). Card inteiro (flat =
// Coins/Diamonds/Gems) agora é o link pro desktop também (antes só o botão
// "Usar na loja", escondido em md:hidden, funcionava e só no mobile).
const NuxtLinkComponent = resolveComponent('NuxtLink');

interface Props {
  title: string;
  prefix: string;
  value: string;
  variant?: 'primary' | 'flat' | 'bonus';
  icon?: string;
  flag?: string;
  // aba que a loja VIP deve abrir já selecionada (coins → points, diamonds,
  // gems) — sem isso o link caía sempre na aba padrão da loja.
  storeTab?: 'points' | 'diamonds' | 'gems';
  // geometria do ícone decorativo de fundo NO MOBILE — box no tamanho
  // NATIVO do ícone (não o bounding box pós-rotação do Figma: top/right já
  // vêm recalculados preservando o centro de rotação, ver BalancesBar.vue),
  // com a rotação aplicada ao próprio box
  iconTop?: number;
  iconRight?: number;
  iconWidth?: number;
  iconHeight?: number;
  iconRotate?: number;
  // geometria do ícone decorativo NO DESKTOP — mantida como estava antes
  // (valores antigos, um único box compartilhado com o brilho), já que o
  // desktop não passou pelo ajuste pixel-perfect feito pro mobile
  desktopIconTop?: number;
  desktopIconRight?: number;
  desktopIconWidth?: number;
  desktopIconHeight?: number;
  desktopIconRotate?: number;
  // ícone pequeno ao lado do título — no mobile o Figma usa o MESMO ícone
  // (mesmo path, só exportado num canvas menor) duas vezes: pequeno aqui e
  // grande/rotacionado no fundo; cada moeda tem sua proporção própria
  titleIconWidth?: number;
  titleIconHeight?: number;
  showActions?: boolean;
  // variant "bonus" com saldo zerado (ENG-893): o card segue visível, mas
  // "Usar saldo"/"Sacar" não têm o que usar/sacar — ficam desabilitados.
  actionsDisabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'flat',
  icon: '',
  flag: '',
  storeTab: undefined,
  iconTop: 0,
  iconRight: -28,
  iconWidth: 100,
  iconHeight: 100,
  iconRotate: 48,
  desktopIconTop: 12,
  desktopIconRight: -28,
  desktopIconWidth: 100,
  desktopIconHeight: 100,
  desktopIconRotate: 48,
  titleIconWidth: 20,
  titleIconHeight: 20,
  showActions: false,
  actionsDisabled: false,
});

const storeLink = computed(() =>
  localeLink(
    props.storeTab ? `/vip/store?tab=${props.storeTab}` : '/vip/store',
  ),
);

// variant "bonus" (Saldo Bônus, ENG-817): "Usar saldo" manda pro mesmo fluxo
// já usado pelo card antigo de bônus (BonusWallet.vue, variant v2) — sports
// se o bônus ativo veio de aposta esportiva, senão cassino.
const walletStore = useWalletStore();
const { rollover } = storeToRefs(walletStore);
const { toggleModal } = useLayoutStore();

onMounted(() => {
  if (props.variant === 'bonus') {
    walletStore.getRolloverData();
  }
});

const bonusUseLink = computed(() =>
  localeLink(
    rollover.value.first_transaction === 'sports'
      ? '/sports'
      : '/games/category/jogar-com-bonus',
  ),
);

// ENG-817: no desktop os cards "primary"/"bonus" não têm os botões de baixo
// (só mobile, ver `md:hidden` abaixo) — sem eles não havia como clicar. O
// card inteiro vira o alvo de clique só no desktop (`isMobile` guarda),
// porque no mobile os botões de dentro já cobrem essas mesmas ações e um
// clique no card por cima deles duplicaria a navegação/abertura de modal.
const { isMobile } = useLayout();

// Clique no card (Saldo BRL ou Saldo Bônus, desktop) leva pra Carteira (tela
// "wallet" do painel V2) — antes o "primary" ia pra /games/category/all e o
// "bonus" abria a modal de regras (o ⓘ já cobre essa segunda ação, ver
// @click.stop nele abaixo; sem o stop os dois cliques se cancelariam).
function onCardClick() {
  if (isMobile.value) return;

  if (props.variant === 'primary' || props.variant === 'bonus') {
    open('wallet');
  }
}
</script>

<template>
  <component
    :is="props.variant === 'flat' ? NuxtLinkComponent : 'div'"
    class="relative flex w-full flex-col gap-[10px] overflow-clip rounded-[16px] p-[16px] md:gap-[4px]"
    :class="[
      props.variant === 'primary' ? 'bg-v2-primary-700' : '',
      props.variant === 'flat'
        ? 'bg-v2-default/40 transition hover:brightness-110'
        : '',
      props.variant !== 'flat'
        ? 'md:cursor-pointer md:transition md:hover:brightness-110'
        : '',
      // gradiente exato do Figma (node 19771:52164) — sem token equivalente
      // em user-v2-tokens.scss, mesma convenção de outros gradientes
      // pontuais do projeto (ex.: DayCell.vue, home-tournaments.vue).
      props.variant === 'bonus'
        ? 'bg-[linear-gradient(130deg,_#1e1b4b_24%,_#4338ca_98%)]'
        : '',
    ]"
    :to="props.variant === 'flat' ? storeLink : undefined"
    @click="props.variant === 'flat' ? close() : onCardClick()"
  >
    <!-- MOBILE: brilho de fundo (Figma "Ellipse 56", mesma geometria nas 3
         moedas) separado do ícone nítido (posição/tamanho/rotação próprios
         por moeda) — geometria validada contra o Figma -->
    <img
      v-if="props.variant === 'flat' && props.icon"
      aria-hidden="true"
      class="pointer-events-none absolute right-[-48px] top-[84px] size-[126px] scale-125 object-contain opacity-70 blur-2xl md:hidden"
      :src="props.icon"
    />

    <div
      v-if="props.variant === 'flat' && props.icon"
      class="pointer-events-none absolute md:hidden"
      :style="{
        top: `${props.iconTop}px`,
        right: `${props.iconRight}px`,
        width: `${props.iconWidth}px`,
        height: `${props.iconHeight}px`,
        transform: `rotate(${props.iconRotate}deg)`,
      }"
    >
      <img
        alt=""
        class="relative size-full object-contain"
        :src="props.icon"
      />
    </div>

    <!-- DESKTOP: mantido como estava antes do ajuste pixel-perfect do mobile
         (brilho + ícone no mesmo box) -->
    <div
      v-if="props.variant === 'flat' && props.icon"
      class="pointer-events-none absolute hidden md:block"
      :style="{
        top: `${props.desktopIconTop}px`,
        right: `${props.desktopIconRight}px`,
        width: `${props.desktopIconWidth}px`,
        height: `${props.desktopIconHeight}px`,
        transform: `rotate(${props.desktopIconRotate}deg)`,
      }"
    >
      <img
        aria-hidden="true"
        class="absolute inset-0 size-full scale-125 object-contain opacity-70 blur-2xl"
        :src="props.icon"
      />
      <img
        alt=""
        class="relative size-full object-contain"
        :src="props.icon"
      />
    </div>

    <div class="relative flex w-full items-center justify-between">
      <p class="text-[14px] font-medium leading-[20px] text-white">
        {{ props.title }}
      </p>
      <img
        v-if="props.flag"
        alt=""
        class="h-[20px] w-[29px] rounded-[5px] object-cover"
        :src="props.flag"
      />
      <img
        v-if="props.variant === 'flat' && props.icon"
        alt=""
        class="shrink-0 object-contain md:hidden"
        :src="props.icon"
        :style="{
          width: `${props.titleIconWidth}px`,
          height: `${props.titleIconHeight}px`,
        }"
      />
      <!-- Regras do bônus reformuladas (Figma node 24415:75893): substitui o
           antigo ícone "gift" decorativo. @click.stop pra não borbulhar pro
           onCardClick do card (que no desktop já abre a mesma modal ao
           clicar em qualquer ponto do card) — sem o stop, os dois disparos
           de toggleModal na mesma tick se cancelariam. É o ÚNICO gatilho no
           mobile: onCardClick sai cedo se isMobile (ver comentário abaixo). -->
      <UserV2Icon
        v-if="props.variant === 'bonus'"
        class="cursor-pointer text-white"
        name="circle-exclamation"
        :size="21"
        @click.stop="toggleModal('bonus-rules')"
      />
    </div>

    <p
      class="relative whitespace-nowrap text-[20px] font-bold leading-[28px] text-white"
    >
      <span class="text-white/30">{{ props.prefix }}</span>
      {{ props.value }}
    </p>

    <UserV2WalletActionButtons
      v-if="props.variant === 'primary'"
      class="relative"
      :class="{ 'md:hidden': !props.showActions }"
      size="card"
    />

    <div
      v-if="props.variant === 'bonus'"
      class="relative flex w-full gap-[6px]"
      :class="{ 'md:hidden': !props.showActions }"
    >
      <NuxtLink
        :aria-disabled="props.actionsDisabled || undefined"
        class="flex h-[32px] flex-1 items-center justify-center rounded-[8px] bg-v2-secondary px-[12px] text-[12px] leading-[16px] text-v2-secondary-foreground"
        :class="{ 'pointer-events-none opacity-40': props.actionsDisabled }"
        :tabindex="props.actionsDisabled ? -1 : undefined"
        :to="bonusUseLink"
        @click="close"
      >
        Usar saldo
      </NuxtLink>
      <button
        class="border-solid flex h-[32px] flex-1 items-center justify-center rounded-[8px] border-2 border-white px-[12px] text-[12px] leading-[16px] text-white disabled:opacity-40"
        :disabled="props.actionsDisabled"
        type="button"
        @click="open('wallet')"
      >
        Sacar
      </button>
    </div>

    <!-- Card inteiro já é o link (ver <component> acima) — isso aqui é só o
         rótulo visual do mobile, sem navegação própria (senão vira <a>
         dentro de <a>). -->
    <span
      v-if="props.variant === 'flat'"
      class="border-solid relative flex h-[32px] w-fit items-center justify-center gap-[8px] rounded-[8px] border-2 border-white px-[12px] text-[12px] leading-[16px] text-white md:hidden"
    >
      Usar na loja
      <UserV2Icon name="chevron-right" />
    </span>
  </component>
</template>
